import { put, call, all, takeLatest, select } from "redux-saga/effects";
import {
  REQPushNotiList,
  RESPushNotiList,
  FailPushNotiList,
  DELPushNotiCampaign
} from "./actions";
import * as actions from "../../Authentication/redux/actions";
import { readCampaigns, removeCampaign } from "../history";
import { authHeaders, notificationInit, notificationRequest } from "../../NotificationList/api";

/*
 * Campaign history: the browser copy is always read, and the notifications the backend stored
 * are fetched and merged in. If that call fails the local copy still renders, flagged with
 * remoteAvailable: false.
 */

/*
 * A notification the backend stored, read as a campaign.
 *
 * The two are not the same object and this page does not pretend otherwise: a notification
 * knows how many people it reached, not how many devices accepted it, so the delivery column
 * stays empty rather than showing a number that means something else. `remote` is what the
 * table reads to decide which actions a row can offer - a row that lives in the database
 * cannot be deleted from this browser's storage, and it is edited by its id rather than
 * copied into a new form.
 */
export const asCampaign = notification => ({
  id: notification.id,
  remote: true,
  code: notification.code,
  name: notification.name || notification.header,
  body: notification.header,
  /*
   * A notification waiting for its scheduled time reads as SCHEDULED, so the time column
   * shows when it goes out rather than when it was stored.
   */
  status:
    notification.scheduledAt && notification.sendStatus === "PENDING"
      ? "SCHEDULED"
      : notification.sendStatus,
  targets: [notification.userScope].filter(Boolean),
  scheduledAt: notification.scheduledAt,
  createdAt: notification.timeSent || notification.scheduledAt
});
function* fetchPushNotiListWorker() {
  const local = yield call(readCampaigns);

  try {
    const accessToken = yield select(state => state.auth.accessToken);
    const tGUID = yield select(state => state.auth.GUID);
    const call = notificationRequest("list", { query: { page: 1, limit: 100 } });
    const request = yield fetch(call.url, notificationInit(call, authHeaders(accessToken, tGUID)));

    if (request.status === 401) {
      yield put(actions.autoSignOut());
      return;
    }

    if (!request.ok) {
      yield put(RESPushNotiList({ campaigns: local, remoteAvailable: false }));
      return;
    }

    const response = yield request.json();
    const rows = Array.isArray(response) ? response : response.data || [];
    const remote = rows.map(asCampaign);
    const remoteIds = remote.map(item => item.id);

    yield put(
      RESPushNotiList({
        campaigns: [...remote, ...local.filter(item => !remoteIds.includes(item.id))],
        remoteAvailable: true
      })
    );
  } catch (error) {
    yield put(RESPushNotiList({ campaigns: local, remoteAvailable: false }));
  }
}

function* deletePushNotiCampaignWorker({ payload }) {
  try {
    yield call(removeCampaign, payload);
    yield call(fetchPushNotiListWorker);
  } catch (error) {
    yield put(FailPushNotiList({ message: error.message }));
  }
}

export function* pushNotiListSaga() {
  yield all([
    takeLatest(REQPushNotiList, fetchPushNotiListWorker),
    takeLatest(DELPushNotiCampaign, deletePushNotiCampaignWorker)
  ]);
}
